import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';

/**
 * Custom hook to fetch meeting configurations for a specific user
 * Follows React Query patterns and avoids manual ViewModel creation
 */
export function useMeetingConfigsForUser(userId: string | null) {
	const userSession = useUserSession();

	return useQuery({
		queryKey: ['scheduler/config', userId],
		queryFn: async () => {
			if (!userId) return null;

			const response = await userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.IMeetingConfigResponse>>(
				Api.ImpersonationBroker.composeApiUrl({
					queryParams: {
						forUserId: userId,
					},
					urlPath: 'scheduler/config',
				}),
				'GET'
			);

			// Transform to view models following the same pattern as useScheduleMeetingSettings
			const user = { id: userId } as Api.IUser; // We only need the ID for the ViewModel constructor
			return response.values.map(config => new Api.MeetingConfigViewModel(userSession, config, user));
		},
		enabled: !!userId,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
}

/**
 * Utility function to get the first published meeting config from a list
 */
export function getFirstPublishedMeetingConfig(
	meetingConfigs: Api.MeetingConfigViewModel[] | undefined
): Api.MeetingConfigViewModel | null {
	if (!meetingConfigs) return null;
	return meetingConfigs.filter(config => config.isPublished)?.[0] || null;
}
